import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Button } from 'components/Button'
import { Dialog, DialogContent } from 'components/Dialog'
import { Eyebrow } from 'components/Eyebrow'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useAnalytics } from 'hooks'
import { cn } from 'lib/utils'
import { selectBusinesses } from 'modules/business'
import { useAppSelector } from 'modules/core'
import { Check } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ClaimableReward } from './referrals.service'
import { useClaimReferralReward } from './use-claim-referral-reward'

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0] ?? '')
    .join('')
    .toUpperCase()

interface ReferralClaimDialogProps {
  /** Controls dialog visibility. Owner (celebration or page) drives this. */
  open: boolean
  /**
   * Called on every close — Escape, overlay click, X, "I'll decide later", or
   * a successful claim. The owner decides whether the close counts as a
   * "dismiss for this id-set" (celebration) or is transient (page).
   */
  onClose: () => void
  /** Rewards to be claimed together in a single POST. */
  rewards: ClaimableReward[]
}

/**
 * T3 celebration dialog for the referrer's earned credits. Shared by the global
 * `ReferralRewardCelebration` (auto-opens when a reward lands) and the "credits
 * waiting to be claimed" card on `ReferralPage` (opens on button click).
 *
 * Adapts to the business count:
 * - 1 business → credits auto-claim into it on open; the modal is a pure
 *   celebration the user acknowledges with "Let's go" (no Claim chore).
 * - 2+ businesses → a picker + "Claim" button, since there's a real decision.
 *
 * Fully controlled — no internal open state. Renders nothing when `open` is
 * false or `rewards` is empty (a claim that already emptied the list, or the
 * page-button was clicked with nothing to claim).
 */
export const ReferralClaimDialog = ({ open, onClose, rewards }: ReferralClaimDialogProps) => {
  const businesses = useAppSelector(selectBusinesses)
  const { claim, claiming } = useClaimReferralReward()
  const { track } = useAnalytics()

  const totalCredits = useMemo(() => rewards.reduce((sum, r) => sum + r.credits, 0), [rewards])

  // One business → no choice to make, so we drop the picker AND the Claim
  // chore: the credits land automatically the moment the modal opens, and the
  // modal is a pure celebration the user just acknowledges. The picker + Claim
  // only earn their place when there's an actual decision (2+ businesses).
  const singleBusiness = businesses.length === 1

  const [pickedBusinessId, setPickedBusinessId] = useState<string | null>(null)

  // Fire "viewed" once per open, and reset the guards when the dialog closes so
  // a later re-open (a new approval) tracks + auto-claims fresh. `didClaim`
  // distinguishes a successful claim from a dismissal so we don't double-count.
  const viewedRef = useRef(false)
  const didClaimRef = useRef(false)
  const autoClaimedRef = useRef(false)
  useEffect(() => {
    if (!open || rewards.length === 0) {
      viewedRef.current = false
      didClaimRef.current = false
      autoClaimedRef.current = false
      return
    }
    if (viewedRef.current) return
    viewedRef.current = true
    track(AnalyticsEvents.Referrals.REFERRAL_CELEBRATION_VIEWED, {
      role: 'referrer',
      credits: totalCredits,
    })
  }, [open, rewards.length, totalCredits, track])

  // Single-business auto-claim: the credits land in their only business the
  // moment the modal opens — no button press required. Fires once per open; the
  // success toast is suppressed because the modal itself is the celebration. A
  // failure surfaces its own error toast and leaves the reward claimable (the
  // sidebar dot + waiting card persist, and it retries on next open).
  useEffect(() => {
    if (!open || rewards.length === 0 || !singleBusiness) return
    if (autoClaimedRef.current) return
    const biz = businesses[0]
    if (!biz) return
    autoClaimedRef.current = true
    void claim(rewards, biz.id, biz.name, { silent: true }).then(result => {
      if (result) didClaimRef.current = true
    })
  }, [open, rewards, singleBusiness, businesses, claim])

  // Dismiss = a close from the picker flow that isn't a successful claim. Fires
  // the "deferred" signal (reward stays claimable) then hands back to the owner.
  // The single-business path never defers (credits already auto-claimed), so it
  // closes via `onClose` directly.
  const handleDismiss = () => {
    if (!didClaimRef.current) {
      track(AnalyticsEvents.Referrals.REFERRAL_CLAIM_DEFERRED, { credits: totalCredits })
    }
    onClose()
  }

  // Default the picker to the first business whenever the dialog opens for a
  // new set of rewards / a business list change (e.g. user switched accounts
  // while the dialog was closed).
  useEffect(() => {
    if (!open) return
    setPickedBusinessId(prev => {
      if (prev && businesses.some(b => b.id === prev)) return prev
      return businesses[0]?.id ?? null
    })
  }, [open, businesses])

  if (!open) return null
  if (rewards.length === 0) return null

  const handleClaim = async () => {
    if (!pickedBusinessId) return
    const biz = businesses.find(b => b.id === pickedBusinessId)
    if (!biz) return
    const result = await claim(rewards, biz.id, biz.name)
    // On success, the referral info refetch empties `claimable`, so the
    // celebration owner will naturally stop rendering this dialog. We still
    // fire onClose so a caller (page) that keeps its own `rewards` prop can
    // reset button state cleanly. Mark the claim so the close isn't counted as
    // a "deferred" dismissal (the grant is tracked server-side as rewarded).
    if (result) {
      didClaimRef.current = true
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) singleBusiness ? onClose() : handleDismiss()
      }}
    >
      <DialogContent variant="dark" size="sm" withCloseButton>
        <div className="flex flex-col items-center text-center">
          {/* Acid hero — the payoff up top, one loud element in the view. */}
          <div className="text-[44px] leading-none font-semibold tracking-[-0.02em] text-acid">
            +{totalCredits}
            <span className="ml-1 text-[22px] font-medium tracking-normal text-acid">cr</span>
          </div>
          <Heading level="h2" className="mt-4">
            A friend you invited just subscribed
          </Heading>
          <Text as="p" variant="body-sm" tone="paper-dim" className="mt-2 max-w-[36ch]">
            {singleBusiness
              ? `Added to ${businesses[0]?.name}. Credits never expire.`
              : 'Pick which business gets the credits. Credits never expire.'}
          </Text>
        </div>

        {/* Business picker — acid border on the selected row, per T3. Only shown
            when there's an actual choice; a single-business user skips it. */}
        {!singleBusiness && (
          <div className="mt-5 space-y-2">
            <Eyebrow>Add credits to</Eyebrow>
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Business">
              {businesses.map(biz => {
                const selected = biz.id === pickedBusinessId
                return (
                  <button
                    key={biz.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setPickedBusinessId(biz.id)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-[10px] border px-3 py-2.5 text-left transition-colors',
                      selected
                        ? 'border-acid bg-bg-3'
                        : 'border-line bg-bg-3 hover:border-line-bright'
                    )}
                  >
                    {biz.logoUrl ? (
                      <img
                        src={biz.logoUrl}
                        alt=""
                        className="h-7 w-7 flex-shrink-0 rounded-[6px] border border-line bg-white object-contain p-0.5"
                      />
                    ) : (
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[6px] border border-line bg-bg-2 text-[11px] font-semibold text-paper-faint">
                        {initialsOf(biz.name)}
                      </div>
                    )}
                    <span className="min-w-0 flex-1 truncate text-[13.5px] text-paper">
                      {biz.name}
                    </span>
                    {selected && <Check size={16} className="flex-shrink-0 text-acid" />}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <div
          className={cn(
            'mt-6 flex items-center gap-2',
            singleBusiness ? 'justify-center' : 'justify-end'
          )}
        >
          {singleBusiness ? (
            // Credits already landed on open — this just acknowledges + closes.
            <Button type="button" variant="t3-primary" size="t3-md" onClick={onClose}>
              Let's go
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="t3-ghost"
                size="t3-md"
                onClick={handleDismiss}
                disabled={claiming}
              >
                I'll decide later
              </Button>
              <Button
                type="button"
                variant="t3-primary"
                size="t3-md"
                onClick={handleClaim}
                disabled={claiming || !pickedBusinessId}
              >
                {claiming ? 'Claiming…' : `Claim +${totalCredits} credits`}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
